import { GridMatrix } from "./grid-matrix";
import { GridItem } from "./grid-item";
import { EventEmitter } from "../event-emitter";

export enum ScaleBorder{
	TOP,
	BOTTOM,
	LEFT,
	RIGHT
}

export class GridEditState extends EventEmitter{
	items:GridItem[] = [];
	cols:number = 0;
	rowHeight:number = 300;
	editMode:boolean = false;
	ignoreHorizontalOnMobile:boolean = false;

	private dragItem:GridItem | null = null;
	private scaleItem:GridItem | null = null;
	private scaleBorder:ScaleBorder = ScaleBorder.TOP;

	private singleColPositions:GridItem[] = [];

	public constructor(items:GridItem[], cols:number){
		super();
		this.items = items;
		this.cols = cols;
	}


	public preventOverlap(){
		let matrix:GridMatrix = new GridMatrix();

		for (let item of this.items){
			if (matrix.isRectEmpty(item.col,item.row, item.width,item.height)){
				matrix.setRect(item.col,item.row, item.width, item.height, item);
			}else{
				let empty = false;
				let y = item.row;
				let x = item.col+1;
				while(!empty) {
					for (; x <= this.cols - item.width; x++){
						empty = matrix.isRectEmpty(x, y, item.width, item.height);
						if (empty){
							break;
						}
					}
					if (!empty) {
						x = 0;
						y++;
					}
				}
				matrix.setRect(x, y, item.width, item.height, item);
				item.col = x;
				item.row = y;
			}
		}

		if (!this.dragItem && !this.scaleItem){
			matrix.removeEmptyRows();
		}

		this.doTransitions();
	}

	private async doTransitions(){
		await new Promise(requestAnimationFrame);
		this.emit("transition");
	}

	public storeRects(){
		this.emit("store-rects");
	}

	public onMouseMove(event:MouseEvent, container:HTMLElement){
		if (!this.editMode) return;
		let containerRect = container.getBoundingClientRect();
		let x = event.pageX - containerRect.left;
		let y = event.pageY - containerRect.top;
		if (this.dragItem) {
			this.onDrag(x,y, container);
		}else if (this.scaleItem){
			this.onScale(x,y, container);
		}
	}

	private onDrag(x:number, y:number, container:HTMLElement){
		if (!this.dragItem) return;
		this.storeRects();
		let colWidth = (container as HTMLElement).clientWidth / this.cols;
		this.dragItem.row = Math.floor(y / this.rowHeight);
		let col = Math.min(Math.floor(x / colWidth), this.cols-1);
		if (col+this.dragItem.width > this.cols){
			col = this.cols - this.dragItem.width;
		}
		this.dragItem.col = col;
		this.resetToTempValues();
		this.preventOverlap();
	}

	private onScale(x:number, y:number, container:HTMLElement){
		if (!this.scaleItem) return;
		this.storeRects();
		let colWidth = (container as HTMLElement).clientWidth / this.cols;
		let col = Math.min(Math.floor(x / colWidth), this.cols-1);
		let row = Math.floor(y / this.rowHeight);
		if (this.scaleBorder == ScaleBorder.RIGHT){
			this.onScaleBorderRight(col, row);
		}else if (this.scaleBorder == ScaleBorder.BOTTOM){
			this.onScaleBorderBottom(col,row);
		}else if (this.scaleBorder == ScaleBorder.LEFT){
			this.onScaleBorderLeft(col, row);
		}else if (this.scaleBorder == ScaleBorder.TOP){
			this.onScaleBorderTop(col, row);
		}
		if (this.scaleItem.height < this.scaleItem.getMinHeight()){
			this.scaleItem.height = this.scaleItem.getMinHeight();
		}
		if (this.scaleItem.width < this.scaleItem.getMinWidth()){
			this.scaleItem.width = this.scaleItem.getMinWidth();
		}
		this.resetToTempValues();
		this.preventOverlap();
	}

	private onScaleBorderLeft(col:number, row:number){
		if (!this.scaleItem) return;
		let originalCol = this.scaleItem.col;
		if (col > originalCol) {
			this.scaleItem.col = col;
			let movedBy = col - originalCol;
			this.scaleItem.width = this.scaleItem.width - movedBy;
			if (this.scaleItem.width < this.scaleItem.getMinWidth()){
				this.scaleItem.width = this.scaleItem.getMinWidth();
			}
		}else{
			this.scaleItem.col = col;
			let movedBy = originalCol - col;
			this.scaleItem.width = movedBy+this.scaleItem.width;
		}
	}

	private onScaleBorderRight(col:number, row:number){
		if (!this.scaleItem) return;
		this.scaleItem.width = Math.max(col - this.scaleItem.col + 1, this.scaleItem.getMinWidth());
	}

	private onScaleBorderBottom(col:number, row:number){
		if (!this.scaleItem) return;
		this.scaleItem.height = Math.max(row - this.scaleItem.row + 1, this.scaleItem.getMinHeight());
	}

	private onScaleBorderTop(col:number,row:number){
		if (!this.scaleItem) return;
		let originalRow = this.scaleItem.row;
		if (row > originalRow) {
			if (this.scaleItem.height <= this.scaleItem.getMinHeight()){
				return;
			}
			this.scaleItem.row = row;
			let movedBy = row - originalRow;
			this.scaleItem.height = this.scaleItem.height - movedBy;
		}else{
			this.scaleItem.row = row;
			let movedBy = originalRow - row;
			this.scaleItem.height = movedBy+this.scaleItem.height;
		}
	}

	private resetToTempValues(){
		for (let item of this.items){
			if (item == this.dragItem){
				continue;
			}
			if (item == this.scaleItem){
				continue;
			}
			item.col = item.tempCol;
			item.row = item.tempRow;
			item.width = item.tempWidth;
			item.height = item.tempHeight;
		}
	}

	public setDragItem(dragItem:GridItem){
		this.dragItem = dragItem;
		this.setTempValues();
		this.setPriorityItem(dragItem);
	}

	private setPriorityItem(item:GridItem){
		let i = this.items.indexOf(item);
		if (i == -1) return;
		this.items.splice(i,1);
		this.items.unshift(item);
	}

	private setTempValues(){
		for (let item of this.items){
			item.tempCol = item.col;
			item.tempRow = item.row;
			item.tempHeight = item.height;
			item.tempWidth = item.width;
		}
	}

	public resetDragItem(){
		this.dragItem = null;
		this.preventOverlap();
	}

	public isDraggingItem(item:GridItem):boolean{
		return this.dragItem == item;
	}

	public isDragging():boolean{
		return this.dragItem != null;
	}


	public onScaleStart(item:GridItem, border:ScaleBorder){
		this.scaleItem = item;
		this.scaleBorder = border;
		this.setTempValues();
		this.setPriorityItem(item);
	}

	public isScaling():boolean{
		return this.scaleItem != null;
	}

	public isScalingItem(item:GridItem):boolean {
		return this.scaleItem == item;
	}

	public resetScaleItem(){
		this.scaleItem = null;
		this.preventOverlap();
	}

	public getRows():number{
		let highestY = 0;
		for (let item of this.items){
			let y = item.row+item.height;
			if (y > highestY){
				highestY = y;
			}
		}
		return highestY;
	}

	public getSingleColumnRows():number{
		let result = 0;
		for (let item of this.items){
			result+=item.height;
		}
		return result;
	}

	public computeSingleColPositions() {
		let sorted = this.items.sort((i,j)=>{
			let diff = i.row - j.row;
			if (diff != 0){
				return diff;
			}
			return i.col - j.col;
		});
		let offset = 0;
		for (let item of sorted){
			item.singleColPosition = offset;
			offset+= item.height;
		}
	}
}